import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../components/PageHeader'
import { Button, Card, Chip, EmptyState, GrantBarrierMark, Input, Modal, Skeleton } from '../components/ui'
import { useGroupsTree } from '../api/hooks'
import { api } from '../api/client'
import { useAuthStore } from '../stores/auth'
import type { GroupKind, GroupNode } from '../api/types'
import { SelectField } from './Directory'

function errText(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

export const GROUP_KINDS: { value: GroupKind; label: string }[] = [
  { value: 'organisation', label: 'Organisation' },
  { value: 'non_profit', label: 'Non-profit' },
  { value: 'private', label: 'Private' },
  { value: 'other', label: 'Other' },
]

export function groupKindLabel(kind: string): string {
  return GROUP_KINDS.find((k) => k.value === kind)?.label ?? kind
}

function countNodes(nodes: GroupNode[]): number {
  return nodes.reduce((n, node) => n + 1 + countNodes(node.children), 0)
}

export function findGroupNode(nodes: GroupNode[], uid: string): GroupNode | null {
  for (const node of nodes) {
    if (node.unique_id === uid) return node
    const hit = findGroupNode(node.children, uid)
    if (hit) return hit
  }
  return null
}

/** Pre-order flatten for parent dropdowns; `barrier` marks grant-inheritance
 *  barriers (inherits_grants === false — suffixed 🔒 in the option label). */
export function flattenGroupTree(
  nodes: GroupNode[],
  depth = 0,
): { uid: string; name: string; depth: number; barrier: boolean }[] {
  return nodes.flatMap((node) => [
    { uid: node.unique_id, name: node.name, depth, barrier: node.inherits_grants === false },
    ...flattenGroupTree(node.children, depth + 1),
  ])
}

/** uid set of a node and everything beneath it (excluded from parent choices). */
export function groupSubtreeIds(node: GroupNode, into: Set<string> = new Set()): Set<string> {
  into.add(node.unique_id)
  for (const child of node.children) groupSubtreeIds(child, into)
  return into
}

/** Map group unique_id → full tree path ("Armborstvägen 12 / Lgh 1203") —
 *  the group-tree analogue of lib/locations' locationPaths. */
export function groupPaths(nodes: GroupNode[]): Map<string, string> {
  const paths = new Map<string, string>()
  const walk = (ns: GroupNode[], trail: string[]) => {
    for (const n of ns) {
      const next = [...trail, n.name]
      paths.set(n.unique_id, next.join(' / '))
      walk(n.children, next)
    }
  }
  walk(nodes, [])
  return paths
}

/** Keep nodes matching the query (name / uid / external ref) plus their ancestors. */
function filterTree(nodes: GroupNode[], q: string): GroupNode[] {
  const needle = q.trim().toLowerCase()
  if (!needle) return nodes
  const walk = (node: GroupNode): GroupNode | null => {
    const children = node.children.map(walk).filter((n): n is GroupNode => n !== null)
    const hit =
      node.name.toLowerCase().includes(needle) ||
      node.unique_id.toLowerCase().includes(needle) ||
      node.external_ref.toLowerCase().includes(needle)
    return hit || children.length > 0 ? { ...node, children } : null
  }
  return nodes.map(walk).filter((n): n is GroupNode => n !== null)
}

export function GroupParentSelect({
  id,
  tree,
  value,
  exclude,
  onChange,
}: {
  id: string
  tree: GroupNode[]
  value: string
  exclude?: Set<string>
  onChange: (uid: string) => void
}) {
  const options = flattenGroupTree(tree).filter((o) => !exclude?.has(o.uid))
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        Parent
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-sm text-ostrel-ink focus:border-ostrel-gold focus:outline-none focus:ring-1 focus:ring-ostrel-gold"
      >
        <option value="">— none (root) —</option>
        {options.map((o) => (
          <option key={o.uid} value={o.uid}>
            {'  '.repeat(o.depth)}
            {o.name}
            {o.barrier ? ' 🔒' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

function CreateGroupModal({
  open,
  tree,
  onClose,
}: {
  open: boolean
  tree: GroupNode[]
  onClose: () => void
}) {
  const scope = useAuthStore((s) => s.customer?.unique_id ?? 'none')
  const queryClient = useQueryClient()

  const [name, setName] = useState('')
  const [kind, setKind] = useState<GroupKind>('organisation')
  const [parentUid, setParentUid] = useState('')
  const [externalRef, setExternalRef] = useState('')
  const [inheritsGrants, setInheritsGrants] = useState(true)

  const create = useMutation({
    mutationFn: () =>
      api.createGroup({
        name: name.trim(),
        kind,
        external_ref: externalRef.trim(),
        parent: parentUid || null,
        inherits_grants: inheritsGrants,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [scope, 'groups'] })
      onClose()
    },
  })

  const reset = () => {
    setName('')
    setKind('organisation')
    setParentUid('')
    setExternalRef('')
    setInheritsGrants(true)
    create.reset()
  }

  // create.reset is stable; only reset when the modal (re)opens.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open) reset()
  }, [open])

  return (
    <Modal open={open} title="New group" onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim()) create.mutate()
        }}
      >
        <Input
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Board members"
          autoFocus
        />
        <SelectField
          label="Kind"
          value={kind}
          onChange={(v) => setKind(v as GroupKind)}
          options={GROUP_KINDS}
        />
        <GroupParentSelect id="create-parent" tree={tree} value={parentUid} onChange={setParentUid} />
        <Input
          label="External ref"
          value={externalRef}
          onChange={(e) => setExternalRef(e.target.value)}
          placeholder="Optional"
        />
        <label className="flex items-center gap-2 text-xs text-ostrel-ink">
          <input
            type="checkbox"
            checked={!inheritsGrants}
            onChange={(e) => setInheritsGrants(!e.target.checked)}
            className="accent-ostrel-gold"
          />
          Require direct assignment (no inherited access)
        </label>
        {create.isError && <p className="text-xs text-ostrel-danger">{errText(create.error)}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="navy" type="submit" disabled={create.isPending || !name.trim()}>
            {create.isPending ? 'Creating…' : 'Create group'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function TreeRow({
  node,
  depth,
  collapsed,
  onToggle,
  onSelect,
}: {
  node: GroupNode
  depth: number
  collapsed: Set<string>
  onToggle: (uid: string) => void
  onSelect: (uid: string) => void
}) {
  const hasChildren = node.children.length > 0
  const isCollapsed = collapsed.has(node.unique_id)
  return (
    <>
      <li>
        <div
          className="flex w-full items-center gap-1.5 rounded-sm py-1.5 pr-2 text-left transition-colors hover:bg-ostrel-sand/60"
          style={{ paddingLeft: `${depth * 14 + 8}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              aria-expanded={!isCollapsed}
              onClick={() => onToggle(node.unique_id)}
              className="flex h-4 w-4 shrink-0 items-center justify-center text-ostrel-ink-muted hover:text-ostrel-ink focus:outline-none"
            >
              <span
                className={`inline-block text-[9px] transition-transform ${isCollapsed ? '' : 'rotate-90'}`}
              >
                ▶
              </span>
            </button>
          ) : (
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              <span className="h-1.5 w-1.5 rounded-full bg-ostrel-dim" />
            </span>
          )}
          <button
            type="button"
            onClick={() => onSelect(node.unique_id)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none"
          >
            <span className="min-w-0">
              <span className="flex items-center gap-1.5">
                <span className="block truncate text-sm font-medium text-ostrel-ink">{node.name}</span>
                {node.inherits_grants === false && <GrantBarrierMark />}
              </span>
              <span className="tnum block truncate text-[11px] text-ostrel-ink-muted">
                {node.unique_id}
              </span>
            </span>
            <span className="ml-auto flex shrink-0 items-center gap-3">
              {node.external_ref && (
                <span className="tnum text-[11px] text-ostrel-ink-muted">{node.external_ref}</span>
              )}
              <Chip
                tone={node.kind === 'organisation' ? 'gold' : 'muted'}
                label={groupKindLabel(node.kind)}
              />
              <span className="tnum w-14 text-right text-[11px] text-ostrel-ink-muted">
                {node.people_count} {node.people_count === 1 ? 'person' : 'people'}
              </span>
              <span className="text-ostrel-ink-muted">→</span>
            </span>
          </button>
        </div>
      </li>
      {!isCollapsed &&
        node.children.map((child) => (
          <TreeRow
            key={child.unique_id}
            node={child}
            depth={depth + 1}
            collapsed={collapsed}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        ))}
    </>
  )
}

export default function GroupsPage() {
  const navigate = useNavigate()
  const tree = useGroupsTree()
  const [search, setSearch] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)

  const visible = useMemo(() => filterTree(tree.data ?? [], search), [tree.data, search])
  const searching = search.trim().length > 0

  const toggle = (uid: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(uid)) next.delete(uid)
      else next.add(uid)
      return next
    })

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        section="Directory"
        title="Groups"
        action={<Button onClick={() => setCreateOpen(true)}>+ New group</Button>}
      />

      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search groups…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search groups"
          />
        </div>
        <span className="tnum shrink-0 text-[11px] text-ostrel-ink-muted">
          {tree.data ? `${countNodes(tree.data)} ${countNodes(tree.data) === 1 ? 'group' : 'groups'}` : ''}
        </span>
      </div>

      <Card title="Group tree">
        {tree.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
            <Skeleton className="h-7" />
          </div>
        ) : tree.isError ? (
          <EmptyState title="Could not load groups" hint={errText(tree.error)} />
        ) : tree.data.length === 0 ? (
          // No duplicate CTA here: the header's '+ New group' action is always
          // visible, so the empty state stays text-only (two identical buttons
          // also raced strict-mode locators while the tree loaded).
          <EmptyState
            title="No groups yet"
            hint="Create the first group to organise people."
          />
        ) : visible.length === 0 ? (
          <EmptyState title={`No groups match “${search.trim()}”`} hint="Try a different search." />
        ) : (
          <ul className="flex flex-col gap-0.5">
            {visible.map((node) => (
              <TreeRow
                key={node.unique_id}
                node={node}
                depth={0}
                collapsed={searching ? new Set() : collapsed}
                onToggle={toggle}
                onSelect={(uid) => navigate(`/groups/${uid}`)}
              />
            ))}
          </ul>
        )}
      </Card>

      <CreateGroupModal open={createOpen} tree={tree.data ?? []} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
